import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  //#region properties
  loginForm: any = FormGroup;
  forgotPasswordForm: any = FormGroup;
  resetPasswordForm: any = FormGroup;
  registerForm: any = FormGroup;
  addInvestorForm: any = FormGroup;
  addVendorForm: any = FormGroup;
  userForm: any = FormGroup;
  investmentForm: any = FormGroup;
  paymentForm: any = FormGroup;
  programForm: any = FormGroup;
  tagsGroupForm: any = FormGroup;
  tagForm: any = FormGroup;
  cancelPaymentForm: any = FormGroup;
  contactForm: any = FormGroup;
  retailProgramInvestmentForm: any = FormGroup;
  confirmationValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      return { required: true };
    } else if (
      control.value !== this.resetPasswordForm.controls.Password?.value
    ) {
      return { confirm: true, error: true };
    }
    return {};
  };
  /**
   * NANP area codes start 2-9, so 0 and 1 cannot open a real number. Mirrors the rule the activation
   * wizard's details step applies (auth-identity-form.component.ts) and the one the API enforces, so a
   * number accepted here is not rejected a screen later. The field is masked, hence the strip.
   *
   * The two faults are separate keys on purpose. `pattern` means the number cannot be dialled;
   * `incomplete` means it is merely unfinished. Reporting both as `pattern` is what told someone
   * halfway through typing 619... that their number starts with 0 or 1.
   */
  nanpMobileValidator = (control: FormControl): { [s: string]: boolean } => {
    const digits = String(control.value ?? '').replace(/\D/g, '');
    if (!digits) {
      return { required: true };
    }
    if (/^[01]/.test(digits)) {
      return { pattern: true };
    }
    return digits.length === 10 ? {} : { incomplete: true };
  };
  //#endregion

  //#region functions
  loginForm_fb() {
    this.loginForm = this._fb.group({
      Email: [
        null,
        [
          Validators.required,
          Validators.pattern(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}/),
        ],
      ],
      Password: [null, [Validators.required]],
    });
  }

  registerForm_fb() {
    this.registerForm = this._fb.group({
      supplierCode: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      firstName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      lastName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      companyName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      mobileNumber: [null, [this.nanpMobileValidator]],
      emailAddress: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[\w+-.]+(\.[\w+-.]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,})$/
          ),
        ],
      ],
      // No password here: the activation email's wizard is where the user sets one (or a passkey).
    });
  }

  addInvestorForm_fb() {
    this.addInvestorForm = this._fb.group({
      // companyName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      firstName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      lastName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      emailAddress: [
        '',
        [Validators.required, Validators.pattern(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}/)],
      ],
      phoneNumber: [''],
      streetAddress1: [''],
      streetAddress2: [''],
      city: [''],
      state: [''],
      zipCode: [''],
      country: [''],
      // isNonAlc: [true],
    });
  }

  addVendorForm_fb() {
    this.addVendorForm = this._fb.group({
      companyName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      vendorDisplayName: ['', [Validators.pattern(/.*\S.*/)]],
      emailAddress: ['', [Validators.email]],
      phoneNumber: [''],
      streetAddress1: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      streetAddress2: ['', [Validators.pattern(/.*\S.*/)]],
      // streetAddress: [''],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipCode: ['', [Validators.required]],
      country: ['', [Validators.required]],
    });
  }

  cancelPaymentForm_fb() {
    this.cancelPaymentForm = this._fb.group({
      reason: [null, [Validators.required]],
    });
  }

  forgotPasswordForm_fb() {
    this.forgotPasswordForm = this._fb.group({
      Email: [null, [Validators.required]],
    });
  }

  resetPasswordForm_fb() {
    this.resetPasswordForm = this._fb.group({
      Password: [null, [Validators.required]],
      ConfirmPassword: [
        null,
        [Validators.required, this.confirmationValidator],
      ],
    });
  }

  // registerForm_fb() {
  //   this.registerForm = this._fb.group({
  //     firstName: ['', Validators.required],
  //     lastName: ['', Validators.required],
  //     phoneNumber: [null, [Validators.required]],
  //     emailAddress: ['', Validators.required],
  //     password: ['', Validators.required],
  //     confirmPassword: ['', Validators.required],
  //   });
  // }

  userForm_fb() {
    this.userForm = this._fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.pattern(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}/)]],
      role: ['', Validators.required],
      location: [''],
      title: [''],
      linkedinUrl: [
        '',
        Validators.pattern(
          /^(|https?:\/\/([\w]+\.)?linkedin\.com\/in\/[A-z0-9_-]+\/?)$/i
        ),
      ],
      phoneNumber: [''],
      sites: [''],
      isUserActive: [true],
    });
  }

  investmentForm_fb() {
    this.investmentForm = this._fb.group({
      investor: ['', Validators.required],
      programPeriod: ['', Validators.required],
      amount: ['', Validators.required],
      scheduledDate: ['', Validators.required],
      description: ['', Validators.required],
    });
  }

  paymentForm_fb() {
    this.paymentForm = this._fb.group({
      vendor: ['', Validators.required],
      programPeriod: ['', Validators.required],
      amount: ['', Validators.required],
      description: ['', Validators.required],
      invoiceNumber: ['', Validators.required],
      tags: ['', Validators.required],
      isNonAlc: [false],
    });
  }

  programForm_fb() {
    this.programForm = this._fb.group({
      label: ['', Validators.required],
      startMonth: ['', Validators.required],
      startYear: ['', Validators.required],
      endMonth: ['', Validators.required],
      endYear: ['', Validators.required],
      isVisible: [true],
    });
  }

  tagsGroupForm_fb() {
    this.tagsGroupForm = this._fb.group({
      tagGroupName: ['', Validators.required],
      // tagGroupColor: ['', Validators.required],
    });
  }

  tagForm_fb() {
    this.tagForm = this._fb.group({
      tagName: ['', Validators.required],
      tagGroup: ['', Validators.required],
    });
  }

  retailProgramInvestmentForm_fb() {
    this.retailProgramInvestmentForm = this._fb.group({
      rep: ['', Validators.required],
      detailedDescription: [''],
      accountingNotes: [''],
      isAgreed: [false],
    });
  }

  contentManagerForm_fb() {
    this.contactForm = this._fb.group({
      firstName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      lastName: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      title: ['', [Validators.pattern(/.*\S.*/)]],
      department: [''],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          ),
        ],
      ],
      phoneNumber: [''],
      notes: ['', [Validators.pattern(/.*\S.*/)]],
      isPrimaryContact: [false],
    });
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      firstName: 'First Name',
      lastName: 'Last Name',
      title: 'Title',
      email: 'Email',
    };
    return labels[fieldName] || fieldName;
  }

  //#endregion
  constructor(private _fb: FormBuilder) { }
}
